/**
 * engine.ts — AudioEngine class + module-level singleton accessor (v2M8.1).
 *
 * Owns:
 *   - One `AudioContext` for the app's lifetime (browsers cap context creation,
 *     so we create exactly one and reuse across session restarts).
 *   - 4-bus mix per AUDIO.md §3.1: `master`, `music`, `sfx`, `ui` gain nodes.
 *     Master gates global mute (M-hotkey, AUDIO §11). The three sub-buses
 *     are exposed for v2M4 Settings volume sliders (wired in v2M8.8).
 *   - The cue registry — `engine.registerProcedural(cueId, bus, build)` /
 *     `engine.registerSample(cueId, bus, buffer)`.
 *   - `playCue(cueId, opts)` — the single entry point every caller uses;
 *     opts carry positional panning + attenuation so cues never need a
 *     retrofit when v2M8.4+ adds spatialised SFX.
 *   - `onFrame(diffState, gameState)` — dispatches `diffCues` and plays
 *     each returned cue. Mirrors `advanceVfx` in the v2M7 VFX layer.
 *
 * Headless-safe: in vitest 'node' env `AudioContext` is undefined; the
 * engine constructor catches and runs as a no-op (every method early-exits).
 *
 * Autoplay gating: most browsers start an `AudioContext` in `suspended`
 * state and block sound until a user gesture. `tryResume` on first click
 * / keydown handles that — wired from `main.ts`.
 */

import type { GameState } from '../state/types';
import { CueRegistry, type CueBus, type CueId, type CueVariation } from './registry';
import { diffCues, type AudioFrameContext, type DiffState } from './diff';
import { buildAmbientBed, type AmbientBedHandle } from './music-bed';

/** Per-call options for `playCue`. */
export interface PlayCueOpts {
  /**
   * Stereo pan, -1 (full left) … 0 (centre) … 1 (full right). Clamped on
   * the engine side. Omit for non-positional cues.
   */
  panX?: number;
  /**
   * Per-call attenuation 0..1 (multiplies the bus gain). Used by §6.2
   * volume ducking on overlap and by §9.1 distance falloff. Omit for
   * full-volume.
   */
  attenuation?: number;
  /**
   * v2M8.4 — multiplier on the cue's base pitch (1.0 = normal). Weapon
   * cues use 0.85..1.15 for variety per AUDIO §6.1. Builders that
   * support variation read this; UI / alert builders ignore it.
   */
  pitchMul?: number;
  /**
   * v2M8.4 — multiplier on the cue's base gain (1.0 = normal). Weapon
   * cues use ±3 dB for variety per AUDIO §6.1. Combines with
   * `attenuation`; both clamp to safe ranges in the builder.
   */
  gainMul?: number;
  /**
   * Force a specific bus regardless of the registry entry's `bus` field.
   * Test / debug only — cue routing is normally registry-driven.
   */
  busOverride?: CueBus;
}

/**
 * AudioEngine — app-scoped audio state. Constructed once via
 * `getAudioEngine()`; reused across session restarts.
 */
export class AudioEngine {
  private readonly audioContext: AudioContext | null;
  private readonly buses: Record<CueBus, GainNode> | null;
  private readonly masterGain: GainNode | null;
  /**
   * v2M8.7 — single-instance ambient music bed handle. `null` when
   * the bed isn't playing. Held here (vs. cue-registry) because the
   * bed is sustained, not fire-once — needs explicit start/stop and
   * graceful fade transitions, which the playCue path doesn't
   * support.
   */
  private ambientBed: AmbientBedHandle | null = null;
  /**
   * v2M8.8 — user-set volume slider values (0..1) per AUDIO §3.1.
   * Master gain is `userMaster * (muted || focusLost ? 0 : 1)`, with
   * a 200 ms ramp on transitions per AUDIO §11. Bus gains track
   * their respective slider values directly. Apply via
   * `applyVolumes`; the M-hotkey and window-blur paths flip
   * `muted` / `focusLost` and re-derive the effective master.
   */
  private userMaster = 0.7;
  private userMusic = 0.5;
  private userSfx = 0.7;
  private userUi = 0.7;
  private muted = false;
  private focusLost = false;
  readonly registry = new CueRegistry();

  constructor() {
    const Ctor = resolveAudioContextCtor();
    if (!Ctor) {
      // Headless test env or unsupported browser. Engine becomes a no-op.
      this.audioContext = null;
      this.buses = null;
      this.masterGain = null;
      return;
    }
    try {
      this.audioContext = new Ctor();
      this.masterGain = this.audioContext.createGain();
      // Seed gains with user-set defaults (0.7 / 0.5 / 0.7 / 0.7)
      // before main.ts loads persisted Settings and calls applyVolumes.
      // Default Web Audio gain is 1.0 which would be too loud, so the
      // pre-applyVolumes window stays mixed reasonably.
      this.masterGain.gain.value = this.userMaster;
      this.masterGain.connect(this.audioContext.destination);
      const music = this.audioContext.createGain();
      const sfx = this.audioContext.createGain();
      const ui = this.audioContext.createGain();
      music.gain.value = this.userMusic;
      sfx.gain.value = this.userSfx;
      ui.gain.value = this.userUi;
      music.connect(this.masterGain);
      sfx.connect(this.masterGain);
      ui.connect(this.masterGain);
      this.buses = { music, sfx, ui };
    } catch {
      // Some browsers (older Safari) may throw on AudioContext construction
      // in non-secure contexts. Fail soft.
      this.audioContext = null;
      this.buses = null;
      this.masterGain = null;
    }
  }

  /** Whether sound is actually plumbed up. False in test env. */
  isReady(): boolean {
    return this.audioContext !== null && this.buses !== null;
  }

  /** Raw bus gain — exposed so v2M8.8 can wire Settings volume sliders. */
  busGain(bus: CueBus): GainNode | null {
    return this.buses ? this.buses[bus] : null;
  }

  /** Master gain — exposed for the M-hotkey global mute (v2M8.8). */
  master(): GainNode | null {
    return this.masterGain;
  }

  /**
   * v3M28.6 — load an MP3 / WAV / OGG over HTTP, decode through the
   * shared `AudioContext`, and register the resulting buffer against
   * the cue id. Until this resolves, any earlier `registerProcedural`
   * call for the same id stays active, so the cue still plays — just
   * via the procedural fallback. Once decoded, the sample replaces
   * the procedural entry and subsequent `playCue` calls use the
   * commissioned audio.
   *
   * Returns a Promise so callers can await load completion when
   * needed (tests, lazy-load checkpoints). Errors are swallowed and
   * logged — a failed sample fetch must not break the cue path; the
   * procedural fallback keeps the cue audible.
   */
  async loadSample(cueId: CueId, bus: CueBus, url: string): Promise<void> {
    if (!this.audioContext) return;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const buffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.registry.registerSample(cueId, bus, buffer);
    } catch (err) {
      // Procedural fallback already registered — keep it active.
      const msg = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.warn(`AudioEngine.loadSample(${cueId}) failed: ${msg}`);
    }
  }

  /**
   * Resume the underlying `AudioContext`. Browsers start it in `suspended`
   * state until a user gesture; call this from a click / keydown listener.
   * Safe to call repeatedly — only the first call has effect.
   */
  tryResume(): void {
    if (!this.audioContext) return;
    if (this.audioContext.state === 'suspended') {
      // resume() returns a promise; we don't await it — best-effort.
      void this.audioContext.resume();
    }
  }

  /**
   * v2M8.7 — start the ambient music bed. Idempotent — repeated calls
   * while the bed is playing are no-ops. Routes through the music
   * bus, fades in over 3 s. Caller (session.ts) starts on session
   * boot; stop on session destroy.
   */
  startAmbientBed(): void {
    if (!this.audioContext || !this.buses) return;
    if (this.ambientBed !== null) return;
    const bed = buildAmbientBed(this.audioContext, this.buses.music);
    for (const node of bed.nodes) {
      try {
        node.start();
      } catch {
        // start() may throw on Safari if called twice — defensive.
      }
    }
    this.ambientBed = bed;
  }

  /**
   * v3M12.3 — fire an end-game stinger (`'music:victory'` or
   * `'music:defeat'`) with proper acoustic spacing. Stops the ambient
   * bed first (1 s fade), waits 1 s for the bed to clear, then plays
   * the stinger. Caller (session.ts) calls once on outcome flip.
   *
   * Wall-clock setTimeout is used purely for the audible gap — the
   * stinger itself is replay-stable (procedural, no RNG).
   */
  playEndGameStinger(cueId: 'music:victory' | 'music:defeat'): void {
    if (!this.audioContext) return;
    this.stopAmbientBed();
    setTimeout(() => {
      this.playCue(cueId);
    }, 1000);
  }

  /**
   * Stop the ambient music bed with a 1 s exponential fade-out, then
   * stop all sources. Idempotent.
   */
  stopAmbientBed(): void {
    if (!this.audioContext || !this.ambientBed) return;
    const { nodes, masterGain } = this.ambientBed;
    this.ambientBed = null;
    const now = this.audioContext.currentTime;
    // Cancel any in-flight ramp (could still be in fade-in), pin
    // current value, then ramp down to silence.
    masterGain.gain.cancelScheduledValues(now);
    const currentValue = masterGain.gain.value;
    masterGain.gain.setValueAtTime(Math.max(currentValue, 0.0001), now);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
    for (const node of nodes) {
      try {
        node.stop(now + 1.05);
      } catch {
        // Defensive — stop may throw on already-stopped sources.
      }
    }
  }

  /**
   * v2M8.8 — apply Settings audio volumes (master / music / sfx / ui)
   * to the bus gains. Stores user-set values so the M-hotkey + focus-
   * loss paths can derive the effective master correctly. Idempotent.
   */
  applyVolumes(s: { master: number; music: number; sfx: number; ui: number }): void {
    this.userMaster = clamp(s.master, 0, 1);
    this.userMusic = clamp(s.music, 0, 1);
    this.userSfx = clamp(s.sfx, 0, 1);
    this.userUi = clamp(s.ui, 0, 1);
    if (!this.audioContext || !this.buses || !this.masterGain) return;
    const now = this.audioContext.currentTime;
    // Master uses the smooth ramp so a slider drag mid-play sounds
    // organic; bus gains snap because they're per-cue routed (audible
    // jump on a slider drag is acceptable per spec — no crossfade
    // requirement for bus-level volume).
    this.applyMasterSmooth();
    this.buses.music.gain.setValueAtTime(this.userMusic, now);
    this.buses.sfx.gain.setValueAtTime(this.userSfx, now);
    this.buses.ui.gain.setValueAtTime(this.userUi, now);
  }

  /**
   * v2M8.8 — toggle the master mute. Per AUDIO §11: "Master-mute
   * hotkey M toggles master gain". Slider value is preserved; only
   * the muted flag flips. Wired to the global keydown listener in
   * main.ts.
   */
  toggleMute(): void {
    this.muted = !this.muted;
    this.applyMasterSmooth();
  }

  /**
   * v2M8.8 — focus-loss flag. Per AUDIO §11: "when the browser tab
   * loses focus, the master gain fades to 0 over 200 ms (doesn't
   * change slider value). Regains on focus return." Wired to window
   * blur / focus listeners in main.ts.
   */
  setFocusLost(lost: boolean): void {
    if (this.focusLost === lost) return;
    this.focusLost = lost;
    this.applyMasterSmooth();
  }

  /**
   * Effective master = userMaster × (muted ? 0 : 1) × (focusLost ? 0 : 1).
   * Both gates read as multiplicative — a player who's muted and tab-
   * switches stays silent on return until they un-mute.
   */
  private applyMasterSmooth(): void {
    if (!this.audioContext || !this.masterGain) return;
    const now = this.audioContext.currentTime;
    const target = this.muted || this.focusLost ? 0 : this.userMaster;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
    this.masterGain.gain.linearRampToValueAtTime(target, now + 0.2);
  }

  /**
   * Play a cue. No-op when the engine isn't ready, or when the cue isn't
   * registered (the latter is the v2M8 silent-stub path for cues whose
   * procedural builder isn't worth synthesising — they're discovered by
   * `diffCues` and just don't make sound until v3M11 ships a sample).
   */
  playCue(cueId: CueId, opts: PlayCueOpts = {}): void {
    if (!this.audioContext || !this.buses) return;
    const entry = this.registry.get(cueId);
    if (!entry) return;
    const bus = opts.busOverride ? this.buses[opts.busOverride] : this.buses[entry.bus];
    // Build the post-source chain (panner → attenuation gain → bus).
    // Always create the chain — even at neutral values — so cue mix has a
    // single shape and v2M8.4+ doesn't need to special-case "spatialised
    // vs not". Cheap to construct; nodes are GC'd when the source ends.
    const target = this.buildPostChain(this.audioContext, bus, opts);
    let source: AudioScheduledSourceNode;
    if (entry.kind === 'procedural') {
      const variation: CueVariation = {
        pitchMul: opts.pitchMul ?? 1,
        gainMul: opts.gainMul ?? 1,
      };
      source = entry.build(this.audioContext, target, variation);
    } else {
      const bs = this.audioContext.createBufferSource();
      bs.buffer = entry.buffer;
      bs.connect(target);
      source = bs;
    }
    try {
      source.start();
    } catch {
      // Some browsers throw if start() is called twice. Defensive.
    }
  }

  /**
   * Frame-level dispatch. Mirrors `advanceVfx` in `src/render/vfx.ts`:
   * `diffCues` observes the gameState transition and returns the cues
   * that fired this frame; we then play each.
   *
   * `frameCtx` carries view mode + camera projection so the diff path
   * can compute positional opts (pan + attenuation) for spatialised
   * cues. Optional — passing nothing yields a non-positional dispatch
   * (used by tests that don't have a render context).
   */
  onFrame(
    diffState: DiffState,
    gameState: GameState,
    frameCtx?: AudioFrameContext,
  ): void {
    if (!this.audioContext) return;
    const events = diffCues(diffState, gameState, frameCtx);
    for (const ev of events) {
      this.playCue(ev.cueId, ev.opts);
    }
  }

  private buildPostChain(
    ctx: AudioContext,
    bus: GainNode,
    opts: PlayCueOpts,
  ): AudioNode {
    const panNode = ctx.createStereoPanner();
    const gainNode = ctx.createGain();
    if (opts.panX !== undefined) panNode.pan.value = clamp(opts.panX, -1, 1);
    if (opts.attenuation !== undefined) gainNode.gain.value = clamp(opts.attenuation, 0, 1);
    panNode.connect(gainNode);
    gainNode.connect(bus);
    return panNode;
  }
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/**
 * Resolve the AudioContext constructor — handles both standard and
 * webkit-prefixed names. Returns null in headless / unsupported envs.
 */
function resolveAudioContextCtor(): typeof AudioContext | null {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  // Older Safari prefix.
  const w = globalThis as unknown as { webkitAudioContext?: typeof AudioContext };
  if (typeof w.webkitAudioContext !== 'undefined') return w.webkitAudioContext;
  return null;
}

/**
 * Module-level singleton. Audio state lives across session restarts —
 * a new game from the main menu reuses the existing AudioContext rather
 * than allocating a fresh one (browsers cap context creation per page).
 */
let singleton: AudioEngine | null = null;

export function getAudioEngine(): AudioEngine {
  if (singleton === null) singleton = new AudioEngine();
  return singleton;
}

/** Test-only — drops the singleton so a fresh engine can be constructed. */
export function _resetAudioEngineForTests(): void {
  singleton = null;
}
